import React, { FC, useCallback } from 'react';
import { useTranslation } from 'services/localization';
import { selectItem, selectItemInitial } from '../../../../preferencesSelectors';
import { ItemIcon } from './components/ItemIcon';
import Spinner from '@atlaskit/spinner';
import { TriState } from '../../../../types';
import { sendItemsFromUI } from '../../../../preferencesSlice';
import { GroupType, ItemStatus } from '../../../../../../services/api';
import { trackSubscriptionUpdate } from '../../../../services/userActionEvents';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import { Box, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Checked from './icons/checked.svg';
import Unchecked from './icons/unchecked.svg';
import Icon from '@atlaskit/icon';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';

const cardStyles = css({
  backgroundColor: token('elevation.surface.raised'),
  paddingTop: token('space.100'),
  paddingBottom: token('space.100'),
  paddingLeft: token('space.200'),
  paddingRight: token('space.200'),
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: token('color.border'),
  borderRadius: '3px',
  cursor: 'pointer',
  color: token('color.text'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: token('space.150'),

  '&:hover': {
    backgroundColor: token('elevation.surface.raised.hovered'),
  },
});

const largeCardStyles = css({
  backgroundColor: token('elevation.surface.raised'),
  paddingTop: token('space.100'),
  paddingBottom: token('space.100'),
  paddingLeft: token('space.200'),
  paddingRight: token('space.200'),
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: token('color.border'),
  borderRadius: '3px',
  cursor: 'pointer',
  color: token('color.text'),
  // Using [media.above.md] here gives a weird parcel error. Hardcoding the media query string until its fixed
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles
  '@media (min-width: 64rem)': {
    gridColumn: 'span 2',
  },

  '&:hover': {
    backgroundColor: token('elevation.surface.raised.hovered'),
  },
});

const largeCardHeaderStyles = xcss({
  paddingBottom: 'space.100',
  cursor: 'pointer',
  color: 'color.text',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'space.150',
});

const largeCardDescriptionStyles = xcss({
  margin: '0',
});

const cardTitleStyles = xcss({
  flexGrow: 1,
});

const cardCheckboxStyles = xcss({
  minHeight: '32px',
  minWidth: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
});

interface OwnProps {
  type: GroupType;
  itemKey: string;
  groupKey: string;
  tabKey?: string;
}

const ItemCard: FC<OwnProps> = ({ type, itemKey, groupKey, tabKey }) => {
  const { translate, keyWithTitle, keyWithDescription } = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectItem(groupKey, itemKey, tabKey));
  const initialState = useAppSelector(selectItemInitial(itemKey));
  const titleTranslated = translate(keyWithTitle(itemKey));
  const descriptionTranslated = translate(keyWithDescription(itemKey));
  const changed = state !== initialState;

  const cardClickHandler = useCallback(() => {
    let targetState = TriState.false;

    if (state !== ItemStatus.ON) targetState = TriState.true;

    if (
      type === GroupType.SUBSCRIPTIONS &&
      (groupKey !== 'product_recommendations' || targetState === TriState.false)
    ) {
      trackSubscriptionUpdate(Boolean(targetState), { source: itemKey });
    }

    dispatch(
      sendItemsFromUI({
        keys: [itemKey],
        state: targetState,
        bypassConsentCheck: false,
        bypassConsentDeclinePrCheck: false,
        skipNotification: false,
        sourceGroup: groupKey,
        sourceItem: itemKey,
        sourceComponent: 'ItemCard',
      }),
    );
  }, [dispatch, groupKey, itemKey, state, type]);

  let element = <Spinner />;
  if (state === ItemStatus.ON && !changed) {
    element = (
      <Icon primaryColor={token('color.icon.selected')} glyph={Checked} label={'Checked'} testId={'checkedSvgIcon'} />
    );
  } else if (state === ItemStatus.ON && changed) {
    element = (
      <Icon
        primaryColor={token('color.icon.success')}
        glyph={Checked}
        label={'Checked'}
        testId={'checked-icon-changed'}
      />
    );
  } else if ((state === ItemStatus.OFF || state === ItemStatus.PENDING) && !changed) {
    element = (
      <Icon
        primaryColor={token('color.icon.selected')}
        glyph={Unchecked}
        label={'Unchecked'}
        testId={'uncheckedSvgIcon'}
      />
    );
  } else if ((state === ItemStatus.OFF || state === ItemStatus.PENDING) && changed) {
    element = (
      <Icon
        primaryColor={token('color.icon.success')}
        glyph={Unchecked}
        label={'Unchecked'}
        testId={'unchecked-icon-changed'}
      />
    );
  }

  if (type === GroupType.SUBSCRIPTIONS) {
    return (
      <div
        css={cardStyles}
        onClick={cardClickHandler}
        data-test-item-key={itemKey}
        data-test-is-checked={state === ItemStatus.ON}
        data-testid={'dynamicConfigElementWrapper'}
      >
        <ItemIcon title={itemKey} />
        <Box xcss={cardTitleStyles}>
          <Heading size={'small'}>{titleTranslated}</Heading>
        </Box>
        <Box xcss={cardCheckboxStyles}>{element}</Box>
      </div>
    );
  } else {
    return (
      <div
        css={largeCardStyles}
        onClick={cardClickHandler}
        data-test-item-key={itemKey}
        data-test-is-checked={state === ItemStatus.ON}
        data-testid={'largeDynamicConfigElementWrapper'}
      >
        <Box xcss={largeCardHeaderStyles}>
          <ItemIcon title={itemKey} />
          <Box xcss={cardTitleStyles}>
            <Heading size={'small'}>{titleTranslated}</Heading>
          </Box>
          <Box xcss={cardCheckboxStyles}>{element}</Box>
        </Box>
        <Box xcss={largeCardDescriptionStyles}>{descriptionTranslated}</Box>
      </div>
    );
  }
};

export default ItemCard;
